import { FunctionComponent, memo, useCallback, useMemo, useState } from "react";
import { IProps } from "./phrase-trigger-samples.types";
import {
    Box,
    BoxContent,
    BoxHeader,
    BoxHeading,
    BoxSubheading,
    Cell,
    Column,
    Heading,
    Table,
    TableColumns,
    ExpandedRow,
    NumericValue,
} from "@edgetier/client-components";
import { Button, SpinnerUntil } from "@edgetier/components";
import { IPhraseTriggerSample, MessageDirection, Route, SearchType } from "@edgetier/watchtower-types";
import { faEllipsisV, faPlus, faSave } from "@fortawesome/pro-solid-svg-icons";
import PhraseTriggerThresholdForm from "./phrase-trigger-threshold-form";
import { useInfiniteQuery } from "react-query";
import { requestPhraseTriggerSamples } from "./phrase-trigger-samples.utilities";
import { useParams } from "react-router-dom";
import { roundTo } from "utilities/round-to";
import { trackUser } from "utilities/track-user";
import { useModal } from "@edgetier/modal";
import usePhraseMessageTranslation from "hooks-for/translations/use-phrase-message-translation";
import PhraseTriggerSampleInteractionPreview from "./phrase-trigger-sample-interaction-preview";
import TranslationButton from "components-for/translations/translation-button";
import PhraseTriggerTranslationSentence from "../phrase-trigger-translation-sentence";
import "./phrase-trigger-samples.scss";

/**
 * Display all potential phrase trigger samples
 * @param props.phraseTriggerValues
 * @param props.tag
 * @param props.submitAction
 */
const PhraseTriggerSamples: FunctionComponent<IProps> = ({
    phraseTriggerValues,
    tag,
    submitPhraseTriggerValues,
    isLoading = false,
}) => {
    const { phraseTriggerId } = useParams<{
        readonly phraseTriggerId: string | undefined;
    }>();

    // Translations for current phrase trigger samples messages
    const { activeTranslations, languagesByIsoCode, userLanguage, toggleTranslation, isTranslationEnabled } =
        usePhraseMessageTranslation();

    /**
     * Store threshold values used for submitting any threshold change from user and
     * current expanded row
     *
     * highlightRowValue - the score of the last sample equal or greater than phrase trigger threshold
     * thresholdValue - latest value changed by user
     */
    const [thresholdData, setThresholdData] = useState<Record<string, number | undefined>>({
        highlightRowValue: typeof phraseTriggerId === "string" ? phraseTriggerValues.similarityThreshold : undefined,
        thresholdValue: undefined,
    });

    const [visiblePhraseSample, setVisiblePhraseSample] = useState<IPhraseTriggerSample | null>(null);
    const { openModal, modalProps } = useModal({ onCloseModal: () => setVisiblePhraseSample(null) });

    const phraseTriggerSamplesParameters = {
        searchType: phraseTriggerValues.searchType,
        search: phraseTriggerValues.phrase,
        messageDirection: [
            ...(phraseTriggerValues.inboundMessages ? [MessageDirection.Inbound] : []),
            ...(phraseTriggerValues.outboundMessages ? [MessageDirection.Outbound] : []),
        ],
        interactionType: phraseTriggerValues.interactionTypes,
        languageIsoCode: phraseTriggerValues.languageIsoCodes,
    };

    /**
     * Hook to fetch phrase trigger samples
     */
    const {
        hasNextPage,
        data,
        fetchNextPage,
        isFetchingNextPage,
        isLoading: isPhraseTriggerSamplesLoading,
    } = useInfiniteQuery(
        [Route.PhraseTriggersSamples, "phraseTriggerSamples", phraseTriggerSamplesParameters],
        ({ pageParam }) =>
            requestPhraseTriggerSamples(Route.PhraseTriggersSamples, phraseTriggerSamplesParameters, pageParam),
        { getNextPageParam: ({ pagination }) => pagination.nextPage }
    );

    // Merge all phrase sample pages into an array and sort descending
    const phraseSamples = useMemo(
        () =>
            (data?.pages ?? [])
                .flatMap<IPhraseTriggerSample>((results) => results.items)
                .sort((one, two) => {
                    if (one.score < two.score) return 1;
                    if (one.score > two.score) return -1;
                    return 0;
                }),
        [data]
    );

    /**
     * Load more samples if there are some available.
     */
    const requestMoreSamples = useCallback(() => {
        fetchNextPage();
        trackUser("Load More Phrase Trigger Samples", { phraseTriggerId });
    }, [fetchNextPage, phraseTriggerId]);

    /**
     * Submit latest threshold value
     */
    const thresholdSubmit = (value: number) => {
        const submitValue = roundTo(value / 100, 4);
        setThresholdData({
            highlightRowValue: submitValue,
            thresholdValue: submitValue,
        });

        trackUser("Set Threshold Value", { phraseTriggerId });
    };

    /**
     * Keys for all rows with a score equal or greater than the selected threshold value.
     * Used to highlighting rows
     */
    const highlightedRowsKeys = useMemo(
        () =>
            phraseSamples.reduce<string[]>(
                (highlightedKeys, row) => [
                    ...highlightedKeys,
                    ...(row.score >= thresholdData.highlightRowValue! ? [row.sentenceId] : []),
                ],
                []
            ),

        [phraseSamples, thresholdData.highlightRowValue]
    );
    /**
     * Open an interaction in a modal.
     * @param phraseSample Selected phrase sample
     */
    const openInteractionInModal = (phraseSample: IPhraseTriggerSample) => {
        setVisiblePhraseSample(phraseSample);
        openModal();
    };

    // Number of phrase samples with a threshold greater or equal than the threshold value
    const phraseSampleCountAboveThreshold = useMemo(() => {
        if (typeof thresholdData.thresholdValue === "number") {
            if (phraseTriggerValues.searchTypeId === SearchType.Similar) {
                const results = phraseSamples.filter((item) => item.score >= thresholdData.thresholdValue!);
                return Array.isArray(results) ? results.length : 0;
            } else {
                return Array.isArray(phraseSamples) ? phraseSamples.length : 0;
            }
        }
        return 0;
    }, [phraseSamples, phraseTriggerValues.searchTypeId, thresholdData.thresholdValue]);

    return (
        <>
            {visiblePhraseSample !== null && (
                <PhraseTriggerSampleInteractionPreview
                    phraseSample={visiblePhraseSample}
                    phraseSamples={phraseSamples}
                    {...modalProps}
                />
            )}
            <Box>
                <BoxHeader>
                    <BoxHeading>
                        <div className="phrase-trigger-samples__heading">
                            <span className="phrase-trigger-samples__heading__title">
                                {phraseTriggerValues.searchTypeId === SearchType.Similar ? "Similar" : "Exact"} Phrase
                                Results
                            </span>
                            <span className="phrase-trigger-samples__heading__phrase">
                                &ldquo;{phraseTriggerValues.phrase}&rdquo;
                            </span>
                            {typeof phraseSamples !== "undefined" && phraseSampleCountAboveThreshold > 0 && (
                                <span className="phrase-trigger-samples__heading__results">
                                    (
                                    {phraseSampleCountAboveThreshold > 1000
                                        ? `> 1,000 matches`
                                        : `${phraseSampleCountAboveThreshold} ${
                                              phraseSampleCountAboveThreshold > 1 ? "matches" : "match"
                                          }`}
                                    )
                                </span>
                            )}
                        </div>
                    </BoxHeading>
                    <BoxSubheading>
                        {typeof phraseTriggerId === "string" &&
                            phraseTriggerValues.searchTypeId !== SearchType.Exact && (
                                <>
                                    <span>Threshold set at </span>
                                    <span>
                                        <NumericValue
                                            value={
                                                (thresholdData.thresholdValue ??
                                                    phraseTriggerValues.similarityThreshold) * 100
                                            }
                                            options={{
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            }}
                                            unit="%"
                                        />
                                    </span>
                                </>
                            )}
                    </BoxSubheading>
                </BoxHeader>
                <BoxContent className="phrase-trigger-samples">
                    <SpinnerUntil<IPhraseTriggerSample[]> data={phraseSamples} isReady={!isPhraseTriggerSamplesLoading}>
                        {(data) => (
                            <div className="phrase-trigger-samples__content">
                                <Table<IPhraseTriggerSample>
                                    getRowKey={({ sentenceId }) => sentenceId}
                                    rows={data}
                                    isInBox
                                    headerIsSticky
                                    onRowClick={(phraseSampleRow) => openInteractionInModal(phraseSampleRow)}
                                    className="phrase-trigger-samples__table"
                                    expandedColumnCount={4}
                                    expandedRowKey={highlightedRowsKeys.at(-1)}
                                    highlightedRowsKeys={highlightedRowsKeys}
                                >
                                    <ExpandedRow<IPhraseTriggerSample>>
                                        {({ score }) => (
                                            <PhraseTriggerThresholdForm
                                                initialValue={roundTo(
                                                    Math.min(
                                                        thresholdData.thresholdValue ??
                                                            phraseTriggerValues.similarityThreshold,
                                                        score
                                                    ) * 100,
                                                    2
                                                )}
                                                tagName={tag.tag}
                                                thresholdSubmit={(value) => thresholdSubmit(value)}
                                            />
                                        )}
                                    </ExpandedRow>
                                    <TableColumns>
                                        {phraseTriggerValues.searchTypeId !== SearchType.Exact && (
                                            <Column>
                                                <Heading>Set Threshold</Heading>
                                                <Cell<IPhraseTriggerSample>>
                                                    {({ score, sentenceId }) => (
                                                        <Button
                                                            styleName="positive"
                                                            outline
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                trackUser("Set Threshold", { sentenceId });
                                                                setThresholdData({
                                                                    thresholdValue: score,
                                                                    highlightRowValue: score,
                                                                });
                                                            }}
                                                        >
                                                            Set Threshold
                                                        </Button>
                                                    )}
                                                </Cell>
                                            </Column>
                                        )}
                                        {phraseTriggerValues.searchTypeId === SearchType.Similar && (
                                            <Column>
                                                <Heading>Similarity (%)</Heading>
                                                <Cell<IPhraseTriggerSample>>
                                                    {({ score }) => (
                                                        <div className="phrase-trigger-samples__threshold">
                                                            <div>
                                                                <NumericValue
                                                                    value={score * 100}
                                                                    options={{
                                                                        minimumFractionDigits: 2,
                                                                        maximumFractionDigits: 2,
                                                                    }}
                                                                    unit="%"
                                                                />
                                                            </div>
                                                            <div className="phrase-trigger-samples__threshold-bar-container">
                                                                <div
                                                                    className="phrase-trigger-samples__threshold-bar"
                                                                    style={{
                                                                        width: `${roundTo(score * 100, 2)}%`,
                                                                    }}
                                                                ></div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Cell>
                                            </Column>
                                        )}
                                        {isTranslationEnabled &&
                                            !data.every(
                                                (phraseEntry) =>
                                                    phraseEntry.languageIsoCode === userLanguage.languageIsoCode
                                            ) && (
                                                <Column>
                                                    <Heading>Language</Heading>
                                                    <Cell<IPhraseTriggerSample>>
                                                        {({ languageIsoCode, sentenceId, sentence }) => (
                                                            <>
                                                                {typeof languageIsoCode === "string" &&
                                                                    userLanguage.languageIsoCode !==
                                                                        languageIsoCode && (
                                                                        <TranslationButton
                                                                            isTranslated={
                                                                                typeof activeTranslations[
                                                                                    sentenceId
                                                                                ] !== "undefined"
                                                                                    ? activeTranslations[sentenceId]
                                                                                          .isTranslated
                                                                                    : false
                                                                            }
                                                                            onClick={() =>
                                                                                toggleTranslation(
                                                                                    sentenceId,
                                                                                    sentence,
                                                                                    languageIsoCode
                                                                                )
                                                                            }
                                                                            textLanguage={
                                                                                languagesByIsoCode[languageIsoCode]
                                                                            }
                                                                            userLanguage={userLanguage}
                                                                        />
                                                                    )}
                                                            </>
                                                        )}
                                                    </Cell>
                                                </Column>
                                            )}
                                        <Column>
                                            <Heading className="phrase-trigger-samples__sentence">
                                                Phrase Sample
                                            </Heading>
                                            <Cell<IPhraseTriggerSample>>
                                                {({ sentence, sentenceId }) => (
                                                    <PhraseTriggerTranslationSentence
                                                        sentence={sentence}
                                                        activeTranslation={activeTranslations[sentenceId]}
                                                    />
                                                )}
                                            </Cell>
                                        </Column>
                                    </TableColumns>
                                </Table>
                                {hasNextPage && (
                                    <div className="phrase-trigger-samples__actions">
                                        <Button
                                            icon={faEllipsisV}
                                            outline
                                            isLoading={isFetchingNextPage !== false}
                                            onClick={requestMoreSamples}
                                        >
                                            Load More Results
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </SpinnerUntil>
                </BoxContent>
            </Box>
            <div className="phrase-trigger-samples__actions">
                <Button
                    icon={typeof phraseTriggerId === "string" ? faSave : faPlus}
                    disabled={
                        (typeof thresholdData.thresholdValue !== "number" &&
                            phraseTriggerValues.searchTypeId !== SearchType.Exact) ||
                        isLoading
                    }
                    isLoading={isLoading}
                    onClick={() =>
                        submitPhraseTriggerValues({
                            ...phraseTriggerValues,
                            similarityThreshold: thresholdData?.thresholdValue!,
                        })
                    }
                >
                    {typeof phraseTriggerId === "string" ? "Save Changes" : "Save"}
                </Button>
            </div>
        </>
    );
};
export default memo(PhraseTriggerSamples);
